<template>
  <content-container
    description="Select a file to view details."
    class="operations-historical"
  >
    <template v-slot:action>
      <mds-search-field
        v-model="searchedFileName"
        label="Search by file name"
        placeholder="Search by File Name"
      />
    </template>
    <fade-transition>
      <mds-loader v-if="filtersLoading" />
      <div
        v-else
        class="operations-historical__content"
      >
        <mds-form class="operations-historical__filters">
          <mds-fieldset horizontal>
            <mds-select
              v-model="selectedProvider"
              label="Provider:"
              :options="providerList"
            />
            <mds-select
              v-model="selectedFiduciary"
              label="Fiduciary:"
              :options="fiduciaryList"
            />
            <mds-select
              v-model="selectedDeliveryMethod"
              label="Delivery Method:"
              :options="deliveryMethodList"
            />
            <mds-select
              v-model="selectedFileType"
              label="File Type:"
              :options="fileTypeList"
            />
            <mds-date-picker
              v-model="selectedDateRange"
              label="Date Range:"
              format="m/d/Y"
              max-date="today"
              mode="range"
              class="operations-historical__date-picker"
            />
          </mds-fieldset>
        </mds-form>
        <template>
          <fade-transition>
            <mds-loader v-if="historyLoading" />
            <mds-data-table
              v-else-if="!historyLoading && chunkedHistoryTableData.length > 0"
              :header-configs="tableHeaders"
              :row-data="chunkedHistoryTableData[currentPage - 1]"
              :pagination-config="tablePaginationConfig"
              pagination="below"
              size="small"
              class="operations-historical__table"
              @mds-data-table-page-change="handleTablePageChange"
            >
              <template v-slot:body-cell="cellData">
                <template v-if="cellData.headerConfig.fieldName === 'fileName'">
                  <a :href="$router.resolve({ name: 'operations-historical-detail', params: { id: cellData.rowData.id } }).href">
                    {{ cellData.rowData[cellData.headerConfig.fieldName] }}
                  </a>
                </template>
                <template v-if="cellData.headerConfig.fieldName === 'fileStatus'">
                  <span
                    :style="`--status-color: var(--color--${cellData.rowData[cellData.headerConfig.fieldName].split(' ').join('-').toLowerCase()});`"
                    class="operations-historical__table-status"
                  >
                    {{ cellData.rowData[cellData.headerConfig.fieldName] }}
                  </span>
                </template>
              </template>
            </mds-data-table>
            <mds-empty-state
              v-else
              title="File Not Found"
              message="There are no files that meet your search criteria."
              size="large"
              class="operations-historical__empty"
            >
              <template v-slot:mds-empty-state-icon>
                <img :src="`${publicPath}images/icons/empty-search.svg`">
              </template>
              <template v-slot:mds-empty-state-actions>
                <mds-button
                  variation="primary"
                  type="button"
                  @click="onClear"
                >
                  Search Again
                </mds-button>
              </template>
            </mds-empty-state>
          </fade-transition>
        </template>
      </div>
    </fade-transition>
  </content-container>
</template>

<script>
import { format, subMonths, parseISO, parse, startOfDay, endOfDay } from 'date-fns'
import { chunk } from '../../../shared/utils'
import { MdsButton } from '@mds/button'
import { MdsDataTable } from '@mds/data-table'
import MdsEmptyState from '@mds/empty-state'
import MdsForm from '@mds/form'
import MdsFieldset from '@mds/fieldset'
import MdsLoader from '@mds/loader'
import MdsSearchField from '@mds/search-field'
import MdsSelect from '@mds/select'
import MdsDatePicker from '../../components/Form/MdsDatePicker/MdsDatePicker'
import FadeTransition from '../../components/Transitions/FadeTransition'
import ContentContainer from '../../components/ContentContainer/ContentContainer'
import sessionStorageMixin from '../../mixins/session-storage'
import { NETWORK_ONLY } from '../../graqhql/fetch-policies'
import { GET_PROVIDERS, GET_IMPORTS, GET_FIDUCIARIES } from '../../graqhql/queries'
import { DATE_FORMAT } from '../../../shared/constants/dates'
import { VALIDATION_STATUS_COPY_MAP } from '../../../shared/constants/validation-statuses'

// Possibly move to Node layer
const DELIVERY_METHODS = [
  'API',
  'FTP',
  'Radar',
]

// Possibly move to Node layer
const FILE_TYPES = [
  'Fund Universe',
]

const TABLE_HEADERS = [
  {
    fieldName: 'fileName',
    text: 'File Name',
    width: '35%',
  },
  {
    fieldName: 'fiduciary',
    text: 'Fiduciary',
    width: '10%',
    align: 'right',
  },
  {
    fieldName: 'deliveryMethod',
    text: 'Delivery Method',
    width: '10%',
    align: 'right',
  },
  {
    fieldName: 'fileType',
    text: 'File Type',
    width: '10%',
    align: 'right',
  },
  {
    fieldName: 'dateAdded',
    text: 'Date Added',
    width: '10%',
    align: 'right',
  },
  {
    fieldName: 'processingStatus',
    text: 'Processing Status',
    width: '15%',
    align: 'right',
  },
  {
    fieldName: 'fileStatus',
    text: 'File Status',
    width: '10%',
    align: 'right',
  },
]

const INITIAL_NUMBER_OF_ROWS_PER_PAGE = 20
const INITIAL_SELECTED_PROVIDER = ''
const INITIAL_SELECTED_FIDUCIARY = ''
const INITIAL_SELECTED_DELIVERY_METHOD = ''
const INITIAL_SELECTED_FILE_TYPE = ''
const INITIAL_SELECTED_DATE_RANGE = [format(subMonths(new Date(), 3), DATE_FORMAT), format(new Date(), DATE_FORMAT)]
const INITIAL_SEARCHED_FILENAME = ''

export default {
  components: {
    MdsButton,
    MdsDataTable,
    MdsEmptyState,
    MdsForm,
    MdsFieldset,
    MdsLoader,
    MdsSelect,
    MdsSearchField,
    MdsDatePicker,
    FadeTransition,
    ContentContainer,
  },
  mixins: [
    sessionStorageMixin({
      properties: [
        'selectedProvider',
        'selectedFiduciary',
        'selectedDeliveryMethod',
        'selectedFileType',
        'selectedDateRange',
      ],
      suffix: 'operationsHistorical',
    }),
  ],
  apollo: {
    fiduciaries: GET_FIDUCIARIES,
    providers: GET_PROVIDERS,
    imports: {
      query: GET_IMPORTS,
      variables () {
        return {
          filters: this.filters,
        }
      },
      fetchPolicy: NETWORK_ONLY,
    },
  },
  data () {
    return {
      publicPath: process.env.BASE_URL,
      // Initialize Apollo properites
      fiduciaries: [],
      providers: [],
      imports: [],
      // End initialize Apollo properties
      selectedProvider: INITIAL_SELECTED_PROVIDER,
      selectedFiduciary: INITIAL_SELECTED_FIDUCIARY,
      selectedDeliveryMethod: INITIAL_SELECTED_DELIVERY_METHOD,
      selectedFileType: INITIAL_SELECTED_FILE_TYPE,
      selectedDateRange: INITIAL_SELECTED_DATE_RANGE,
      selectedDateRangeFilter: INITIAL_SELECTED_DATE_RANGE,
      searchedFileName: INITIAL_SEARCHED_FILENAME,
      deliveryMethods: DELIVERY_METHODS,
      fileTypes: FILE_TYPES,
      currentPage: 1,
      numResultsPerPage: INITIAL_NUMBER_OF_ROWS_PER_PAGE,
      tableHeaders: TABLE_HEADERS,
    }
  },
  computed: {
    filtersLoading () {
      return this.$apollo.queries.fiduciaries.loading ||
        this.$apollo.queries.providers.loading
    },
    historyLoading () {
      return this.$apollo.queries.imports.loading
    },
    providerList () {
      return [{ text: 'All' }, ...this.providers.map(({ code }) => ({ text: code, value: code }))]
    },
    deliveryMethodList () {
      return [{ text: 'All' }, ...this.deliveryMethods.map(method => ({ text: method, value: method }))]
    },
    fiduciaryList () {
      return [
        { text: 'All' },
        ...this.fiduciaries.map(fiduciary => ({ text: fiduciary.name, value: fiduciary.code })),
      ]
    },
    fileTypeList () {
      return [
        { text: 'All' },
        ...this.fileTypes.map(fileType => ({ text: fileType, value: fileType })),
      ]
    },
    filters () {
      const [start, end] = this.selectedDateRangeFilter
      return {
        ...(start && end ? { start: startOfDay(parse(start, DATE_FORMAT, new Date())), end: endOfDay(parse(end, DATE_FORMAT, new Date())) } : {}),
        ...(this.selectedProvider ? { provider: this.selectedProvider } : {}),
        ...(this.selectedDeliveryMethod ? { uploadMethod: this.selectedDeliveryMethod } : {}),
        ...(this.selectedFiduciary ? { fiduciary: this.selectedFiduciary } : {}),
        ...(this.selectedFileType ? { fileType: this.selectedFileType } : {}),
      }
    },
    filteredHistory () {
      return this.imports.filter(item => this._applySearch(item, this.searchedFileName))
    },
    historyTableData () {
      return this.filteredHistory.map(item => ({
        id: item.id,
        fileName: item.displayedFilename,
        dateAdded: item.uploadDate ? format(parseISO(item.uploadDate), DATE_FORMAT) : '—',
        fiduciary: item.fiduciary || '—',
        fileType: item.fileType || '—',
        deliveryMethod: this._displayDeliveryMethod(item),
        fileStatus: item.validationStatus ? VALIDATION_STATUS_COPY_MAP[item.validationStatus] : '—',
        processingStatus: item.processingStatus || '—',
      }))
    },
    chunkedHistoryTableData () {
      return chunk(this.historyTableData, this.numResultsPerPage)
    },
    tablePaginationConfig () {
      return {
        pageSize: INITIAL_NUMBER_OF_ROWS_PER_PAGE,
        totalItems: this.historyTableData.length,
        showItemsInfo: true,
        showItemsSelect: true,
      }
    },
  },
  watch: {
    selectedDateRange: {
      handler (newVal, oldVal) {
        if (newVal.length !== 1) {
          this.selectedDateRangeFilter = newVal
        } else {
          this.selectedDateRangeFilter = oldVal
        }
      },
    },
    imports: {
      handler () {
        this.currentPage = 1
      },
    },
  },
  methods: {
    onClear () {
      this.selectedProvider = INITIAL_SELECTED_PROVIDER
      this.selectedFiduciary = INITIAL_SELECTED_FIDUCIARY
      this.selectedDeliveryMethod = INITIAL_SELECTED_DELIVERY_METHOD
      this.selectedFileType = INITIAL_SELECTED_FILE_TYPE
      this.selectedDateRange = INITIAL_SELECTED_DATE_RANGE
      this.searchedFileName = INITIAL_SEARCHED_FILENAME
    },
    handleTablePageChange ({ page, pageSize }) {
      this.numResultsPerPage = pageSize === -1 ? this.historyTableData.length : pageSize
      this.currentPage = page
    },
    _applySearch (item, searchedValue) {
      if (!searchedValue) {
        return true
      }
      return item.transformedFilename.toLowerCase().includes(searchedValue.toLowerCase()) ||
        item.displayedFilename.toLowerCase().includes(searchedValue.toLowerCase())
    },
    _displayDeliveryMethod (item) {
      const { uploadMethod, uploadBy } = item
      return uploadMethod === 'Radar' ? `${uploadMethod} - ${uploadBy}` : uploadMethod
    },
  },
}
</script>

<style lang="scss">
@mixin status-indicator {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    margin-right: $mds-space-half-x;
    border-radius: $mds-border-radius-round;
    background-color: var(--status-color);
  }
}

.operations-historical {
  --color--success: #009592;
  --color--partial: #{$mds-feedback-color-warning};
  --color--failed: #{$mds-feedback-color-error};
  --color--schema-failed: #{$mds-color-violet-46};
}

.operations-historical__content {
  display: grid;
  gap: $mds-space-4-x;
  justify-items: center;
  align-items: start;
}

.operations-historical__loader {
  padding: $mds-space-8-x 0;
}

.operations-historical__filters,
.operations-historical__table {
  justify-self: stretch;
}

.operations-historical__table-status {
  @include status-indicator(); // Status type set inline above
}
</style>

<style lang="scss" module>
// MDS overrides
@import '@mds/link';

:global(.operations-historical__filters) {
  .mds-fieldset__horizontal > .mds-label {
    flex: 1 0 0;
    margin-bottom: 0;
  }
}

:global(.operations-historical__table) {
  .mds-data-table__cell {
    a {
      @include mds-link($in-table: true, $visited-styles: false);
    }
  }
}

:global(.operations-historical__empty) {
  .mds-empty-state__icon > * {
    max-width: 100%;
  }
}
</style>
